import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Alert,
  AlertTitle,
  Stack,
} from '@mui/material';
import {
  getFormattedDateShortMonthDDYYYY,
  Icons,
  OutlinedIcons,
} from '@marageti/z4-lib';
import { TripRequest as TR } from '@marageti/z4-sdk/lib/travel';
import {
  ViewProps,
  TripRequestProps,
  PastTripProps,
} from './types';
import config from '../../config';
import ReviewNotificationBadge from '../../components/trip-request-review-notification-badge';
import TripRequestStatusChip from '../../components/chips/trip-request';
import {
  DESKTOP_IMAGE_HEIGHT,
  MOBILE_IMAGE_HEIGHT,
  Section,
  SectionSubtitle,
  SectionTitle,
  TripRequestImage,
} from './utils';
import AnnouncementBanner from './announcement-banner';
import MinimalChatIndicator from '../../components/trip-request-chat-dot';
import ArchivedTripsButton from './archived-trips-button';

const { OutlinedTicketIcon } = OutlinedIcons;

const NewTripRequest = () => (
  <Card
    sx={{
      width: '100%',
    }}
  >
    <CardActionArea
      onClick={() => window.open(`${config.Z3_URL}trip-request`, '_blank')}
      sx={{
        height: {
          desktop: DESKTOP_IMAGE_HEIGHT,
          mobile: MOBILE_IMAGE_HEIGHT,
        },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      <CardContent>
        <Typography variant="body1" fontWeight="bold">
          New Trip
          <br />
          Request
        </Typography>
      </CardContent>
    </CardActionArea>
  </Card>
);

const TripRequest = ({
  tripRequest,
  travelerId,
  showReviewBadge,
  isPast = false,
}: TripRequestProps) => {
  const navigate = useNavigate();
  const {
    id, createdAt, photoId, destinations,
  } = tripRequest;

  const countryString = `${destinations[0].name}${destinations.length > 1 ? ` and ${destinations.length - 1}+` : ''}`;

  const handleClick = () => navigate(`/trip-requests/${id}`);

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
      }}
    >
      <TripRequestImage photoId={photoId} />
      {showReviewBadge && (
        <ReviewNotificationBadge
          badgeProps={{
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          }}
        />
      )}
      <Box sx={{ mb: 1.5, mt: 2.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="h3" noWrap>{countryString}</Typography>
          <MinimalChatIndicator userId={travelerId} tripMessageState={tripRequest.tripMessageState} />
        </Stack>
        <Typography variant="caption" color="text.primary">
          {`Created ${getFormattedDateShortMonthDDYYYY(createdAt) || ''}`}
        </Typography>
      </Box>

      <Box>
        <TripRequestStatusChip tripRequest={tripRequest} size="medium" isPast={isPast} />
      </Box>
    </Box>

  );
};

const PastTrip = ({
  id,
  travelerId,
  countryName,
  createdAtDate,
  isLast,
  showReviewBadge,
  tripMessageState,
}: PastTripProps) => {
  const navigate = useNavigate();
  const handleClick = () => navigate(`/trip-requests/${id}`);
  return (
    <Box
      sx={{
        marginBottom: `${isLast ? '64px' : '24px'}`,
        borderBottom: `${isLast ? 'none' : '1px solid #C2C5C5'}`,
        cursor: 'pointer',
      }}
      onClick={handleClick}
    >
      <Box display="flex" mb={0.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          {showReviewBadge && <ReviewNotificationBadge useIcon />}
          <Typography noWrap variant="h4">
            {countryName}
          </Typography>
          <MinimalChatIndicator userId={travelerId} tripMessageState={tripMessageState} />
        </Stack>
      </Box>

      <Typography
        variant="subtitle1"
        sx={{
          display: 'block',
          marginBottom: '20px',
        }}
      >
        {`Created ${getFormattedDateShortMonthDDYYYY(createdAtDate) || ''}`}
      </Typography>
    </Box>
  );
};

const TravelHistory = ({ pastTrips, travelerId, reviewPromptTrips }: { pastTrips: TR[], travelerId: string, reviewPromptTrips: string[] }) => {
  if (!pastTrips) {
    return null;
  }
  return (
    <>
      {pastTrips.map((trip, index) => {
        const countriesString = trip.destinations.map((destination) => destination.name).join(', ');
        const showReviewBadge = reviewPromptTrips.includes(trip.id) && !trip.visibilityFlags.hideReviewRequest;
        return (
          <PastTrip
            key={trip.id}
            id={trip.id}
            countryName={countriesString}
            createdAtDate={trip.createdAt}
            isLast={index === pastTrips.length - 1}
            status={trip.status}
            showReviewBadge={showReviewBadge}
            travelerId={travelerId}
            tripMessageState={trip.tripMessageState}
          />
        );
      })}
    </>
  );
};

const HomeView = ({
  travelerId,
  activeTrips,
  pastTrips,
  archivedTrips,
  reviewPromptTrips,
  loading,
  error,
  showAnnouncementBanner,
}: ViewProps) => (
  <>
    <Container maxWidth="xl">
      {showAnnouncementBanner && <AnnouncementBanner />}
      <Section
        icon={<Icons.Sparkles />}
        title={<SectionTitle title="Active Trips" />}
        subtitle={(
          (!activeTrips.length && !loading) && (
            <>
              <SectionSubtitle
                subtitle="Submit a new Trip Request and let us plan your next dream vacation!"
              />
              <Link
                to="https://www.zicasso.com/l/de/luxury"
                target="_blank"
                style={{
                  color: 'inherit',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body" sx={{ color: '#4A5152', textDecoration: 'underline', mt: 2 }}>
                  Discover your next destination
                </Typography>
              </Link>
            </>
          )
        )}
        sx={{
          // Override top padding just for the first section.
          paddingTop: {
            desktop: 6,
            mobile: 3.5,
          },
        }}
      >
        <Grid container spacing={2.5}>
          {activeTrips && activeTrips.map((trip) => (
            <Grid key={trip.id} item xs={6} sm={3} md={3} lg={3}>
              <TripRequest
                travelerId={travelerId}
                tripRequest={trip}
                showReviewBadge={reviewPromptTrips.includes(trip.id) && !trip.visibilityFlags.hideReviewRequest}
                isPast={false}
              />
            </Grid>
          ))}
          <Grid item xs={6} sm={3} md={3} lg={3}>
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
              }}
            >
              <NewTripRequest />
            </Box>
          </Grid>
        </Grid>
      </Section>
    </Container>

    <Box sx={{ backgroundColor: 'background.paper' }}>
      <Container maxWidth="xl">
        <Section
          icon={<OutlinedTicketIcon />}
          title={<SectionTitle title="Travel History" />}
          subtitle={<SectionSubtitle subtitle="Your previous experiences with Zicasso." />}
        >
          <TravelHistory pastTrips={pastTrips} travelerId={travelerId} reviewPromptTrips={reviewPromptTrips} />
          {(!loading && archivedTrips.length > 0) && <ArchivedTripsButton archivedTrips={archivedTrips} />}
          {(!loading && error) && (
            <Alert severity="error">
              <AlertTitle>There was an error getting archived trips.</AlertTitle>
              {`Code: ${error.code}: ${error.message}`}
              <br />
              {error.id && `ID: ${error.id}`}
            </Alert>
          )}
        </Section>
      </Container>
    </Box>
  </>
);

export default HomeView;
